<script setup>
import { computed, ref, onMounted, onUnmounted, nextTick, watch } from 'vue';

const props = defineProps({
  settings: {
    type: Object,
    default: () => ({}),
  },
});

// Get values from settings with defaults
const images = computed(() => {
  const imgs = props.settings.images || [];
  // Handle both Elementor gallery format and simple array format
  if (Array.isArray(imgs)) {
    return imgs.map(img => ({
      url: img.url || img.src || img,
      alt: img.alt || ''
    }));
  }
  return [];
});

// Handle both simple number and Elementor slider format {size: number, unit: string}
const getSliderValue = (val, defaultVal, includeUnit = false) => {
  let size;
  let unit = 'px';

  if (typeof val === 'object' && val !== null && 'size' in val) {
    size = parseFloat(val.size);
    unit = val.unit || 'px';
  } else if (val !== undefined && val !== null && val !== '') {
    size = parseFloat(val);
  }

  // Use default only if size is NaN or undefined
  if (size === undefined || isNaN(size)) {
    size = defaultVal;
  }

  if (includeUnit) {
    return `${size}${unit}`;
  }
  return size;
};

// Track current viewport width for responsive values
const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);

// Get responsive value based on viewport (Elementor breakpoints: mobile < 768, tablet < 1025)
const getResponsiveValue = (baseKey, defaultVal) => {
  const settings = props.settings;
  const width = viewportWidth.value;

  // Mobile: < 768px
  if (width < 768) {
    const mobileVal = settings[`${baseKey}_mobile`];
    if (mobileVal !== undefined && mobileVal !== '' && mobileVal !== null) {
      return getSliderValue(mobileVal, defaultVal);
    }
  }

  // Tablet: < 1025px
  if (width < 1025) {
    const tabletVal = settings[`${baseKey}_tablet`];
    if (tabletVal !== undefined && tabletVal !== '' && tabletVal !== null) {
      return getSliderValue(tabletVal, defaultVal);
    }
  }

  // Desktop (default)
  return getSliderValue(settings[baseKey], defaultVal);
};

const speed = computed(() => getResponsiveValue('speed', 10));
const spacing = computed(() => getSliderValue(props.settings.spacing, 0));
const imageHeight = computed(() => getSliderValue(props.settings.image_height, 48));
const direction = computed(() => props.settings.direction || 'ltr');
const pauseOnHover = computed(() => props.settings.pause_on_hover !== 'no');

// Gradient settings - styles handled by Elementor selectors
const gradientEnabled = computed(() => props.settings.gradient_enabled === 'yes');

// Track element and animation
const trackRef = ref(null);
const animationDuration = ref(0); // Start at 0 to prevent animation until calculated
const isReady = ref(false); // Track if animation is ready to start

// Calculate animation duration based on content width
function calculateDuration() {
  nextTick(() => {
    if (trackRef.value && images.value.length > 0) {
      // One set is exactly half of the track (since we duplicate 2x)
      const oneSetWidth = trackRef.value.scrollWidth / 2;
      const pixelsPerSecond = speed.value || 10; // Fallback to prevent division issues

      // Ensure we have a valid width
      if (oneSetWidth > 0 && pixelsPerSecond > 0) {
        animationDuration.value = oneSetWidth / pixelsPerSecond;
        isReady.value = true;
      } else {
        animationDuration.value = 20; // Fallback
        isReady.value = true;
      }
    }
  });
}

// Recalculate on resize
let resizeTimer = null;
function handleResize() {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    viewportWidth.value = window.innerWidth;
    calculateDuration();
  }, 250);
}

// Recalculate duration when speed changes (due to responsive breakpoints)
watch(speed, () => {
  calculateDuration();
});

onMounted(() => {
  // Use requestAnimationFrame to ensure DOM is painted before calculating
  // This is more reliable than setTimeout and syncs with browser render cycle
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      calculateDuration();
    });
  });
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  clearTimeout(resizeTimer);
});

// Repeat logos enough times to ensure seamless loop (duplicated 2x after)
const REPEAT_COUNT = 2;

// Create a single "set" of logos repeated enough times to fill wide screens
const oneSetOfLogos = computed(() => {
  if (images.value.length === 0) return [];

  let result = [];
  for (let i = 0; i < REPEAT_COUNT; i++) {
    result = result.concat(images.value);
  }
  return result;
});

// Duplicate the set exactly 2x for seamless -50% loop
const duplicatedImages = computed(() => {
  if (oneSetOfLogos.value.length === 0) return [];
  return [...oneSetOfLogos.value, ...oneSetOfLogos.value];
});

// Dynamic styles
const trackStyle = computed(() => ({
  '--animation-duration': `${animationDuration.value}s`,
  '--image-height': `${imageHeight.value}px`,
  '--item-spacing': `${spacing.value}px`,
}));

const trackClass = computed(() => ({
  'logo-carousel-track': true,
  'logo-carousel-track--ltr': direction.value === 'ltr' && isReady.value,
  'logo-carousel-track--rtl': direction.value === 'rtl' && isReady.value,
  'logo-carousel-track--pause-hover': pauseOnHover.value,
}));
</script>

<template>
  <div class="logo-carousel">
    <div v-if="images.length === 0" class="logo-carousel-empty">
      <p>No logos added. Add images in the widget settings.</p>
    </div>
    <template v-else>
      <!-- Left gradient overlay -->
      <div
        v-if="gradientEnabled"
        class="logo-carousel-gradient logo-carousel-gradient--start"
      ></div>

      <div class="logo-carousel-row">
        <div
          ref="trackRef"
          :class="trackClass"
          :style="trackStyle"
        >
          <div
            v-for="(image, index) in duplicatedImages"
            :key="index"
            class="logo-carousel-item"
          >
            <img
              :src="image.url"
              :alt="image.alt"
              class="logo-carousel-image"
            />
          </div>
        </div>
      </div>

      <!-- Right gradient overlay -->
      <div
        v-if="gradientEnabled"
        class="logo-carousel-gradient logo-carousel-gradient--end"
      ></div>
    </template>
  </div>
</template>

<style>
.logo-carousel {
  overflow: hidden;
  position: relative;
  width: 100%;
}

.logo-carousel-empty {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  border-radius: 0.5rem;
}

.logo-carousel-row {
  overflow: hidden;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
}

.logo-carousel-gradient {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 2;
  pointer-events: none;
}

.logo-carousel-gradient--start {
  left: 0;
}

.logo-carousel-gradient--end {
  right: 0;
}

.logo-carousel-track {
  display: flex;
  align-items: center;
  width: max-content;
}

.logo-carousel-track--ltr {
  animation: scrollLogosLTR var(--animation-duration, 20s) linear infinite;
}

.logo-carousel-track--rtl {
  animation: scrollLogosRTL var(--animation-duration, 20s) linear infinite;
}

.logo-carousel-track--pause-hover:hover {
  animation-play-state: paused;
}

.logo-carousel-item {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding-left: calc(var(--item-spacing, 0px) / 2);
  padding-right: calc(var(--item-spacing, 0px) / 2);
}

.logo-carousel .logo-carousel-image {
  height: var(--image-height, 48px);
  width: auto;
  object-fit: contain;
}

@keyframes scrollLogosLTR {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

@keyframes scrollLogosRTL {
  0% {
    transform: translateX(-50%);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
